import { Component, OnInit, ViewChild } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus, AuthError } from '@azure/msal-browser';
import { catchError, filter } from 'rxjs/operators';
import { UserService } from './services/user.service';
import { Event, NavigationEnd, Router } from '@angular/router';
import { LoaderInterceptor } from './core/interceptor/loader.interceptor';
import { PublicRequestService, publicRoutes } from './services/public-request.service';
import { LocalStorageService } from './services/local-storage.service';
import { LocalStorageKeys } from './core/enums/LocalStorageKeys';
import { MyAccountService } from './pages/my-account/services/my-acount.service';
import { UserProfile } from './pages/my-account/interfaces/company-settings.interface';
import { setTime } from 'ngx-bootstrap/chronos/utils/date-setters';
import { RoleFilterService } from './services/role-filter.service';
import { RoleFilterDropdownRowComponent } from './shared/role-filter-dropdown-row/role-filter-dropdown-row.component';
import { AlertData, AlertService } from './core/services/alert.service';
import { of, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth/auth.service';
import { EMPTY_USER_PROFILE } from './core/constants/empty-user-profile';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title = 'cafler-frontend';
  checkIsCollapsed = false;
  isMsalInitialized = false;

  // showAlertService: boolean = true;

  public isPublicRoute = true;

  alertData: AlertData | null = null;
  private subs = new Subscription();

  constructor(
    private MsalService: MsalService,
    private router: Router,
    private userService: UserService,
    private msalBroadcastService: MsalBroadcastService,
    private LocalStorageService: LocalStorageService,
    private myAccountService: MyAccountService,
    private alertService: AlertService,
    private translateService: TranslateService,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.checkIsCollapsed = !!this.LocalStorageService.get(LocalStorageKeys.IsNavbarCollapsed)
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        const currentUrl = event.urlAfterRedirects;
        this.isPublicRoute = publicRoutes.some(route => currentUrl.startsWith(route));
        if (!this.isPublicRoute) {
          this.setupMsalListeners()
        }
      });
    this.setAlertMessage()
  }

  getUser() {
    if (this.isPublicRoute) { return; }

    const cached = this.myAccountService.getUserDetailLocal();
    if (cached) {
      this.handleUserProfile(cached);
      return;
    }

    this.myAccountService.getUserDetail().pipe(
      catchError((error) => {
        console.error('[App Component]: Error fetching user details:', error);
        this.myAccountService.setUserDetailLocal(EMPTY_USER_PROFILE);
        return of(null);
      })
    ).subscribe((data: UserProfile | null) => {
      if (data) {
        this.myAccountService.setUserDetailLocal(data);
        this.handleUserProfile(data);
      } else {
        // fail-open: si falla /user/me, dejamos entrar para no tumbar el marketplace
        this.isMsalInitialized = true;
      }
    })
  }

  /**
   * Bloqueo de conductores en el marketplace: si IsInternalEmployee tiene valor
   * (true=interno, false=externo) la cuenta es de conductor y la mandamos a la página
   * pública /access-denied. Se usa `!= null` (NO `!==`): null o ausente (undefined) → no es
   * conductor → marketplace normal. Lo hacemos aquí (con MSAL ya en reposo) en vez de en un
   * guard para no disparar errores de token/recargas durante la navegación.
   *
   * `isMsalInitialized` solo se pone a true para cuentas NO conductor: el contenido privado
   * está oculto hasta entonces, así el conductor nunca llega a ver el marketplace (no flash).
   */
  private handleUserProfile(user: UserProfile): void {
    if (user.IsInternalEmployee != null) {
      this.router.navigate(['/access-denied']);
      return;
    }
    this.isMsalInitialized = true;
  }

  private setupMsalListeners(): void {
    this.msalBroadcastService.inProgress$
      .pipe(filter((status) => status === InteractionStatus.None))
      .subscribe({
        next: () => {
          if (this.MsalService.instance.getAllAccounts().length > 0) {
            this.userService.refreshUserInfo();
            this.getUser()
          } else {
            this.throwLogout()
          }
        },
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        )
      )
      .subscribe({
        next: (msg: EventMessage) => {
          // this.router.navigate(['/login']);
        },
        error: (error) => console.error(error),
      });

    this.MsalService.instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_FAILURE && event.error) {
        const error = event.error as AuthError;
        if (error.errorCode === 'access_denied' && error.errorMessage.includes('AADB2C90091')) {
          this.throwLogout()
          // this.router.navigate(['/login']);
        }
      }
    });
  }

  setAlertMessage() {
    this.subs.add(
      this.alertService.alert$.subscribe((alert) => {
        this.alertData = alert;
      })
    );
  }

  changeCheck(newItem: boolean) {
    this.checkIsCollapsed = newItem;
    this.LocalStorageService.set(LocalStorageKeys.IsNavbarCollapsed, this.checkIsCollapsed)
  }


  onCloseAlert(): void {
    this.alertService.clearAlert();
  }

  throwLogout() {
    this.authService.logout();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
